import { getIsOnline } from './connectivity';
import {
  getDirty,
  clearDirty,
  applyPulled,
  getLastPull,
  setLastPull,
} from './userDataStore';

// Sync engine donnees user (backend.md section 6). Sync naif assume : flag dirty
// tenant lieu d'outbox, push LWW, pull deltas, dernier-ecrit-gagne sur updated_at.
// Reutilise getIsOnline() de connectivity (pas de detection dupliquee).

// Backoff exponentiel plafonne a 60s (spec 6.4). Reset au succes.
export const BACKOFF_MS = Object.freeze([1000, 2000, 4000, 8000, 16000, 32000, 60000]);

const realSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Une passe de sync : push des rows dirty, puis pull des deltas depuis lastPull.
// Leve en cas d'echec reseau (le caller decide du retry). No-op si offline ou
// sans token (session anonyme pas encore posee).
export async function syncUserData(
  db,
  { apiUrl, online = getIsOnline(), getToken, fetchImpl = fetch } = {}
) {
  if (!online) return { offline: true };

  const token = getToken ? await getToken() : null;
  if (!token) return { skipped: 'no-token' };

  const authHeaders = { Authorization: `Bearer ${token}` };

  // --- Push : envoie toutes les rows dirty, puis deflague celles acquittees. ---
  const dirty = await getDirty(db);
  if (dirty.length) {
    const operations = dirty.map((r) => ({
      scope: r.scope,
      item_key: r.item_key,
      data: r.data,
      deleted: r.deleted,
      updated_at: r.updated_at,
    }));
    const res = await fetchImpl(`${apiUrl}/api/sync/push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ operations }),
    });
    if (!res.ok) throw new Error(`push HTTP ${res.status}`);
    await res.json();
    // Acquittement : on deflague la version exacte poussee (clearDirty ne touche
    // pas une row reecrite entre-temps). LWW serveur transparent ici.
    for (const op of operations) {
      await clearDirty(db, op.scope, op.item_key, op.updated_at);
    }
  }

  // --- Pull : applique les deltas serveur depuis le dernier curseur. ---
  const since = (await getLastPull(db)) || '';
  const pullUrl = `${apiUrl}/api/sync/pull${since ? `?since=${encodeURIComponent(since)}` : ''}`;
  const pullRes = await fetchImpl(pullUrl, { headers: authHeaders });
  if (!pullRes.ok) throw new Error(`pull HTTP ${pullRes.status}`);
  const { changes = [], server_time } = await pullRes.json();
  for (const change of changes) {
    await applyPulled(db, change);
  }
  if (server_time) await setLastPull(db, server_time);

  return { offline: false, pushed: dirty.length, pulled: changes.length };
}

// Sync avec retry exponentiel. Arrete apres maxAttempts echecs consecutifs
// (attente du prochain event online). `sleep` injectable pour les tests.
export async function syncUserDataWithRetry(
  db,
  { sleep = realSleep, maxAttempts = BACKOFF_MS.length, ...opts } = {}
) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await syncUserData(db, opts);
    } catch (err) {
      const isLast = attempt === maxAttempts - 1;
      if (isLast) return { failed: true, error: err.message };
      await sleep(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]);
    }
  }
  return { failed: true };
}
